import React, { useState, useEffect } from 'react';
import styled from "styled-components";
import { Terminal, Key, Copy, AlertCircle, Trash2, Check } from "lucide-react";
import Cookies from 'js-cookie';
import storage from 'utils/Storage';
import { API, sendNotfication } from 'utils/Helper';
import { formatUserDate } from 'utils/timezoneHelper';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background-color: #122129 !important;
  border-radius: 8px !important;
  padding: 24px !important;
  border: 1px solid #2f4553 !important;

  @media (max-width: 768px) {
    padding: 16px !important;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: bold;
  color: #fff;
  margin: 0;
`;

const Description = styled.p`
  color: #8e969f;
  font-size: 14px;
  margin: 0 0 20px 0;
  line-height: 1.6;
`;

const Divider = styled.div`
  height: 1px !important;
  background-color: #2f4553 !important;
  margin: 20px 0 !important;
`;

const ActionButton = styled.button`
  height: 44px !important;
  padding: 0 24px !important;
  border-radius: 6px !important;
  background-color: #1475e1 !important;
  color: #fff !important;
  font-weight: bold !important;
  font-size: 13px !important;
  border: none !important;
  transition: all 0.2s !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;

  &:hover:not(:disabled) {
    background-color: #1880f0 !important;
  }

  &:disabled {
    background-color: #122129 !important;
    color: #4f575e !important;
    border: 1px solid #2f4553 !important;
    cursor: not-allowed !important;
  }
`;

const DangerButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(239, 68, 68, 0.1);
  }
`;

const InfoBox = styled.div`
  background-color: rgba(59, 130, 246, 0.1) !important;
  border: 1px solid rgba(59, 130, 246, 0.2) !important;
  border-radius: 6px !important;
  padding: 16px !important;
  display: flex !important;
  gap: 12px !important;
  margin-bottom: 20px !important;
`;

const InfoText = styled.div`
  color: #3b82f6;
  font-size: 14px;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
  max-width: 400px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #8e969f;
  font-weight: 500;
`;

const Input = styled.input`
  height: 44px !important;
  background-color: #0f212e !important;
  border: 1px solid #2f4553 !important;
  border-radius: 6px !important;
  padding: 0 16px !important;
  color: #fff !important;
  font-size: 14px !important;
  outline: none !important;
  box-shadow: none !important;
  transition: border-color 0.2s !important;

  &:focus {
    border-color: #1475e1 !important;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  text-align: left;
`;

const Th = styled.th`
  padding: 10px 16px;
  border-bottom: 1px solid #2f4553;
  color: #8e969f;
  font-weight: 600;
  position: sticky;
  top: 0;
  background-color: #122129 !important;
  z-index: 1;
`;

const Td = styled.td`
  padding: 10px 16px;
  border-bottom: 1px solid #2f4553;
  color: #fff;
  vertical-align: middle;
`;

const TableWrapper = styled.div`
  max-height: 200px !important;
  overflow-y: auto !important;
  overflow-x: auto !important;
  margin-top: 12px !important;
  border: 1px solid #2f4553 !important;
  border-radius: 6px !important;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #0f212e;
  }
  &::-webkit-scrollbar-thumb {
    background: #2f4553;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #3d5a6d;
  }
`;

const FormRow = styled.div`
  display: flex !important;
  gap: 10px !important;

  @media (max-width: 768px) {
    flex-direction: column !important;
    align-items: stretch !important;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const PageBtn = styled.button`
  height: 32px;
  padding: 0 12px;
  background-color: ${props => props.active ? "#1fc16b" : "#0f212e"};
  color: ${props => props.active ? "#000" : "#8e969f"};
  border: 1px solid #2f4553;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #1fc16b;
    color: ${props => props.active ? "#000" : "#fff"};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  gap: 16px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #0f212e;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #2f4553;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #122129 !important;
  border: 1px solid #2f4553;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WarningBox = styled.div`
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 6px;
  padding: 16px;
  display: flex;
  gap: 12px;
`;

const WarningText = styled.div`
  color: #f87171;
  font-size: 13px;
  line-height: 1.5;
`;

const GeneratedKeyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #0f212e;
  border: 1px solid #2f4553;
  border-radius: 6px;
  padding: 12px 16px;
  font-family: monospace;
  color: #1fc16b;
  font-size: 14px;
  word-break: break-all;
  gap: 12px;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: #8e969f;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    color: #fff;
    background-color: #3a4142;
  }
`;

const ApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [keyName, setKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const getUid = () => {
    let uid = Cookies.get('uid');
    if (!uid) {
      try {
        const token = storage.getKey('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          uid = payload.id || payload.uid;
        }
      } catch (e) {}
    }
    return uid;
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const uid = getUid();
      if (!uid) return;
      setLoading(true);
      const res = await fetch(`${API}/api-keys/list/${uid}`);
      const data = await res.json();
      if (data.success) {
        setApiKeys(data.apiKeys || []);
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!keyName.trim()) return sendNotfication('Please enter a key name', 'error');

    try {
      const uid = getUid();
      setLoading(true);
      const res = await fetch(`${API}/api-keys/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, name: keyName })
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedKey(data.apiKey);
        setShowModal(true);
        setKeyName('');
        setCurrentPage(1);
        fetchKeys();
      } else {
        sendNotfication(data.error || 'Failed to generate API Key', 'error');
      }
    } catch (err) {
      sendNotfication('Network error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API Key? Any applications using this key will immediately lose access.')) return;

    try {
      const uid = getUid();
      setLoading(true);
      const res = await fetch(`${API}/api-keys/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, keyId })
      });
      const data = await res.json();
      if (data.success) {
        sendNotfication('API Key revoked successfully', 'success');
        fetchKeys();
      } else {
        sendNotfication(data.error || 'Failed to revoke API Key', 'error');
      }
    } catch (err) {
      sendNotfication('Network error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedKey);
    setCopied(true);
    sendNotfication('API Key copied to clipboard', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  // Pagination logic
  const totalPages = Math.ceil(apiKeys.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentKeys = apiKeys.slice(indexOfFirstItem, indexOfLastItem);

  // Adjust page if items deleted
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [apiKeys, totalPages, currentPage]);

  return (
    <PageContainer>
      <Card>
        <CardHeader>
          <Terminal size={24} color="#3b82f6" />
          <Title>API Access</Title>
        </CardHeader>
        <Description>
          Manage your API keys for programmatic access to your account.
        </Description>
        <Divider />

        <InfoBox>
          <AlertCircle size={20} color="#3b82f6" style={{ flexShrink: 0, marginTop: '2px' }} />
          <InfoText>
            <strong>Security Warning:</strong> API keys provide full access to your account. Never share your keys with anyone, and do not commit them to public repositories.
          </InfoText>
        </InfoBox>

        <form onSubmit={handleGenerate}>
          <FormGroup>
            <Label>Create New API Key</Label>
            <FormRow>
              <Input
                type="text"
                placeholder="e.g. Trading Bot"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                required
                disabled={loading}
                style={{ flexGrow: 1 }}
              />
              <ActionButton type="submit" disabled={loading || !keyName.trim()}>
                Generate
              </ActionButton>
            </FormRow>
          </FormGroup>
        </form>

        <Divider />

        <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px' }}>Your API Keys</h3>

        {loading && apiKeys.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>Loading keys...</div>
        ) : apiKeys.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <Key size={32} color="#9ca3af" />
            </EmptyIcon>
            <div style={{ color: '#fff', fontWeight: 'bold' }}>No API Keys</div>
            <div style={{ color: '#9ca3af', fontSize: '14px', maxWidth: '300px' }}>
              You haven't generated any API keys yet. Create one to get started with our API.
            </div>
          </EmptyState>
        ) : (
          <>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Key Preview</Th>
                    <Th>Created At</Th>
                    <Th style={{ textAlign: 'center' }}>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {currentKeys.map((k) => (
                    <tr key={k.id}>
                      <Td style={{ fontWeight: '600' }}>{k.name}</Td>
                      <Td style={{ fontFamily: 'monospace', color: '#1fc16b' }}>{k.key_preview}</Td>
                      <Td>{formatUserDate(k.created_at)}</Td>
                      <Td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <DangerButton onClick={() => handleRevoke(k.id)} title="Revoke API Key">
                            <Trash2 size={18} />
                          </DangerButton>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>

            {totalPages > 1 && (
              <PaginationContainer>
                <PageBtn 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </PageBtn>
                {[...Array(totalPages)].map((_, i) => (
                  <PageBtn 
                    key={i} 
                    active={currentPage === i + 1} 
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </PageBtn>
                ))}
                <PageBtn 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </PageBtn>
              </PaginationContainer>
            )}
          </>
        )}
      </Card>

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff', margin: 0 }}>API Key Generated</h3>
            <WarningBox>
              <AlertCircle size={20} color="#f87171" style={{ flexShrink: 0, marginTop: '2px' }} />
              <WarningText>
                Please copy your API key now. For your security, **this key will not be shown again**. If you lose it, you will have to generate a new one.
              </WarningText>
            </WarningBox>
            
            <GeneratedKeyWrapper>
              <span style={{ overflowWrap: 'anywhere' }}>{generatedKey}</span>
              <CopyButton onClick={handleCopy} title="Copy to clipboard">
                {copied ? <Check size={18} color="#1fc16b" /> : <Copy size={18} />}
              </CopyButton>
            </GeneratedKeyWrapper>

            <ActionButton onClick={() => setShowModal(false)} style={{ alignSelf: 'flex-end', marginTop: '12px' }}>
              I've Copied the Key
            </ActionButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default ApiKeys;
